import { useState, useRef, useEffect, useCallback } from "react";

import c1 from "assets/images/Client1.png";
import c2 from "assets/images/Client2.png";
import c3 from "assets/images/Client3.png";
import c4 from "assets/images/Client4.png";
import c5 from "assets/images/Client5.png";
import c6 from "assets/images/Client6.png";
import c7 from "assets/images/Client7.png";
import c8 from "assets/images/Client8.png";
import c9 from "assets/images/Client9.png";
import c10 from "assets/images/Client10.png";
import c11 from "assets/images/Client11.png";
import c12 from "assets/images/Client12.jpeg";
import c13 from "assets/images/Client13.jpeg";
import c14 from "assets/images/Client14.jpeg";
import c15 from "assets/images/Client15.jpeg";
import c16 from "assets/images/Client16.jpeg";
import c17 from "assets/images/Client17.jpeg";
import c18 from "assets/images/Client18.jpeg";
import c19 from "assets/images/Client19.jpeg";
import c20 from "assets/images/Client20.jpeg";
import c21 from "assets/images/Client21.jpeg";
import c22 from "assets/images/Client22.jpeg";
import c23 from "assets/images/Client23.jpeg";
import c24 from "assets/images/Client24.jpeg";
import c25 from "assets/images/Client25.jpeg";
import c26 from "assets/images/Client26.jpeg";

import styles from "./MorphGallery.module.css";

const IMAGES = [
  c1,
  c2,
  c3,
  c4,
  c5,
  c6,
  c7,
  c8,
  c9,
  c10,
  c11,
  c12,
  c13,
  c14,
  c15,
  c16,
  c17,
  c18,
  c19,
  c20,
  c21,
  c22,
  c23,
  c24,
  c25,
  c26,
];

// ── WebGL shaders ─────────────────────────────────────────────────────────────

const VERT_SRC = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAG_SRC = `
precision highp float;
uniform sampler2D u_from;
uniform sampler2D u_to;
uniform float u_progress;
uniform vec2 u_resolution;
uniform float u_fromAspect;
uniform float u_toAspect;
uniform float u_scale;
uniform float u_direction;
varying vec2 v_uv;

vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x,289.0); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
  vec2 i=floor(v+dot(v,C.yy)); vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
  vec4 x12=x0.xyxy+C.xxzz; x12.xy-=i1; i=mod(i,289.0);
  vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
  vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
  m=m*m*m*m;
  vec3 x=2.0*fract(p*C.www)-1.0; vec3 h=abs(x)-0.5;
  vec3 ox=floor(x+0.5); vec3 a0=x-ox;
  m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
  vec3 g; g.x=a0.x*x0.x+h.x*x0.y; g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.0*dot(m,g);
}
float fbm(vec2 v) {
  float val=0.0,amp=0.5;
  for(int i=0;i<5;i++){val+=amp*snoise(v);v*=2.0;amp*=0.5;}
  return val;
}
vec2 containUV(vec2 uv,float imgAspect) {
  float ca=u_resolution.x/u_resolution.y;
  vec2 sc=(ca>imgAspect)?vec2(ca/imgAspect,1.0):vec2(1.0,imgAspect/ca);
  return (uv-0.5)*sc+0.5;
}
bool inBounds(vec2 uv) {
  return uv.x>=0.0&&uv.x<=1.0&&uv.y>=0.0&&uv.y<=1.0;
}
void main() {
  float edge=0.15;
  float ap=u_progress*(1.0+2.0*edge)-edge;
  float noise=fbm(v_uv*u_scale+vec2(0,u_progress*u_direction))*.5+.5;
  noise=smoothstep(0.,2.,length(texture2D(u_to,containUV(v_uv,u_fromAspect)).rgb)+noise);
  float mf=1.0-smoothstep(ap-edge,ap+edge,noise);
  vec2 fromUV=containUV(v_uv+vec2(0,noise*u_progress*0.5*u_direction),u_fromAspect);
  vec2 toUV=containUV(v_uv+vec2(0,noise*(1.0-u_progress)*-0.25*u_direction),u_toAspect);
  vec4 bg=vec4(0.05,0.05,0.05,1.0);
  vec4 fromColor=inBounds(fromUV)?texture2D(u_from,fromUV):bg;
  vec4 toColor=inBounds(toUV)?texture2D(u_to,toUV):bg;
  gl_FragColor=mix(fromColor,toColor,mf);
}`;

// ── WebGL helpers ─────────────────────────────────────────────────────────────

function compileShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const e = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error(e);
  }
  return s;
}

function createProgram(gl, vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, compileShader(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compileShader(gl, gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  return p;
}

function loadImage(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}

function uploadTexture(gl, img) {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  return tex;
}

// ── MorphRenderer ─────────────────────────────────────────────────────────────

class MorphRenderer {
  constructor(canvas) {
    this._c = canvas;
    this._gl = null;
    this._u = null;
    this._textures = [];
    this._aspects = [];
    this._from = 0;
    this._to = 0;
    this._progress = 1;
    this._start = null;
    this._dur = 1400;
    this._dir = 1;
    this._raf = null;
    this._ready = false;
    this._dead = false;
  }

  async init(urls) {
    const gl = this._c.getContext("webgl");
    if (!gl) return;
    this._gl = gl;
    this._ro = new ResizeObserver(() => this._resize());
    this._ro.observe(this._c);
    const prog = createProgram(gl, VERT_SRC, FRAG_SRC);
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a_position");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this._u = {
      from: gl.getUniformLocation(prog, "u_from"),
      to: gl.getUniformLocation(prog, "u_to"),
      progress: gl.getUniformLocation(prog, "u_progress"),
      resolution: gl.getUniformLocation(prog, "u_resolution"),
      fromAspect: gl.getUniformLocation(prog, "u_fromAspect"),
      toAspect: gl.getUniformLocation(prog, "u_toAspect"),
      scale: gl.getUniformLocation(prog, "u_scale"),
      direction: gl.getUniformLocation(prog, "u_direction"),
    };
    const imgs = await Promise.all(urls.map(loadImage));
    this._textures = imgs.map((img) => uploadTexture(gl, img));
    this._aspects = imgs.map((img) => img.naturalWidth / img.naturalHeight);
    this._ready = true;
    this._loop();
  }

  go(from, to) {
    if (!this._ready || from === to) return;
    this._from = from;
    this._to = to;
    this._progress = 0;
    this._start = performance.now();
    this._dir = to > from ? 1 : -1;
  }

  _resize() {
    const gl = this._gl;
    if (!gl) return;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(this._c.clientWidth * dpr);
    const h = Math.round(this._c.clientHeight * dpr);
    if (!w || !h) return;
    this._c.width = w;
    this._c.height = h;
    gl.viewport(0, 0, w, h);
  }

  _loop() {
    if (this._dead) return;
    this._render();
    this._raf = requestAnimationFrame(() => this._loop());
  }

  _render() {
    const gl = this._gl;
    if (!gl || !this._ready) return;
    if (this._start !== null && this._progress < 1) {
      const t = Math.min((performance.now() - this._start) / this._dur, 1);
      this._progress = t < 0.5 ? 16 * t ** 5 : 1 - (-2 * t + 2) ** 5 / 2;
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._textures[this._from]);
    gl.uniform1i(this._u.from, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this._textures[this._to]);
    gl.uniform1i(this._u.to, 1);
    gl.uniform1f(this._u.progress, this._progress);
    gl.uniform2f(this._u.resolution, this._c.width, this._c.height);
    gl.uniform1f(this._u.fromAspect, this._aspects[this._from]);
    gl.uniform1f(this._u.toAspect, this._aspects[this._to]);
    gl.uniform1f(this._u.scale, 3.5);
    gl.uniform1f(this._u.direction, this._dir);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  destroy() {
    this._dead = true;
    cancelAnimationFrame(this._raf);
    this._ro?.disconnect();
    if (this._gl) this._textures.forEach((t) => this._gl.deleteTexture(t));
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

function MorphGallery() {
  const [active, setActive] = useState(0);
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const prevRef = useRef(0);
  const intervalRef = useRef(null);

  const resetAutoplay = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const next = (prevRef.current + 1) % IMAGES.length;
      rendererRef.current?.go(prevRef.current, next);
      prevRef.current = next;
      setActive(next);
    }, 4500);
  }, []);

  useEffect(() => {
    const renderer = new MorphRenderer(canvasRef.current);
    rendererRef.current = renderer;
    renderer.init(IMAGES);
    resetAutoplay();
    return () => {
      renderer.destroy();
      clearInterval(intervalRef.current);
    };
  }, [resetAutoplay]);

  const goTo = useCallback(
    (next) => {
      const idx = (next + IMAGES.length) % IMAGES.length;
      rendererRef.current?.go(prevRef.current, idx);
      prevRef.current = idx;
      setActive(idx);
      resetAutoplay();
    },
    [resetAutoplay]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.stage}>
        <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />

        <button className={styles.navPrev} onClick={() => goTo(active - 1)} aria-label="Previous">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <button className={styles.navNext} onClick={() => goTo(active + 1)} aria-label="Next">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <ul className={styles.thumbs}>
          {IMAGES.map((src, i) => (
            <li key={i} className={styles.thumbItem}>
              <button
                className={styles.thumbButton}
                aria-current={active === i ? "true" : "false"}
                onClick={() => goTo(i)}
              >
                <img src={src} alt={`Cliente ${i + 1}`} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default MorphGallery;
